import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  TextField
} from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { httpRequest } from 'src/services/httpRequest';

interface OTPVerificationFormData {
  otp: string[];
}

// Define the expected location state type
interface LocationState {
  email: string;
}

function OTPVerificationPage() {
  const { state: townshipState, township, uuid } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [formData, setFormData] = useState<OTPVerificationFormData>({
    otp: ['', '', '', '']
  });

  // Get email from navigation state with proper typing
  const locationState = location.state as LocationState | null;
  const email = locationState?.email;

  // Refs for OTP input fields
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate(`/${townshipState}/${township}/${uuid}/forgot-password`, {
        replace: true
      });
    }
  }, [email, navigate]);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0 && !canResend) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanResend(true);
    }
  }, [countdown, canResend]);

  const handleOTPChange = (index: number, value: string) => {
    // Only allow numbers
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOTP = [...formData.otp];
      newOTP[index] = value;
      setFormData({ otp: newOTP });

      // Clear error when user starts typing
      if (error) setError('');
      if (success) setSuccess('');

      // Auto-focus next input
      if (value !== '' && index < 3) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Handle backspace
    if (e.key === 'Backspace' && formData.otp[index] === '' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (/^[0-9]+$/.test(pastedData)) {
      const newOTP = pastedData.split('').concat(Array(4).fill('')).slice(0, 4);
      setFormData({ otp: newOTP });

      // Focus the next empty field or last field
      const nextEmptyIndex = newOTP.findIndex((digit) => digit === '');
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 3;
      otpRefs.current[focusIndex]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = formData.otp.join('');

    if (otpString.length !== 4) {
      setError('Please enter complete OTP');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await httpRequest(
        'POST',
        `/auth/verify-reset-password-otp/${townshipState}/${township}/${uuid}`,
        {
          EmailID: email,
          OTP: otpString
        }
      );

      if (response?.status === 'success') {
        setSuccess('OTP verified successfully');
        // Navigate to reset password page
        setTimeout(() => {
          navigate(
            `/${townshipState}/${township}/${uuid}/forgot-password/reset-password`,
            {
              state: {
                email: email,
                otpToken: response.data?.ResetToken || otpString // Token from API or OTP as fallback
              }
            }
          );
        }, 1500);
      } else {
        setError(response?.message || 'Invalid OTP. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await httpRequest('POST', `/auth/reset-password-otp/${townshipState}/${township}/${uuid}`, {
        EmailID: email
      });

      if (response?.status === 'success') {
        setSuccess('New OTP has been sent to your email');
        setFormData({ otp: ['', '', '', ''] });
        setCanResend(false);
        setCountdown(60);
        // Focus first input
        otpRefs.current[0]?.focus();
      } else {
        setError(
          response?.message || 'Failed to resend OTP. Please try again.'
        );
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToForgotPassword = () => {
    navigate(`/${townshipState}/${township}/${uuid}/forgot-password`);
  };

  return (
    <Box
      sx={{
        minHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <IconButton
          onClick={handleBackToForgotPassword}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.light'
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, color: 'primary.main' }}>
          Back
        </Typography>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          mt: 8
        }}
      >
        <Container maxWidth="sm">
          <Card
            sx={{
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              overflow: 'visible'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <form onSubmit={handleSubmit}>
                <Box sx={{ p: 4, pb: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    Verify OTP
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 1 }}
                  >
                    We've sent a 4-digit verification code to
                  </Typography>

                  <Typography
                    variant="body2"
                    color="primary.main"
                    align="center"
                    sx={{ mb: 3, fontWeight: 'medium' }}
                  >
                    {email}
                  </Typography>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: '8px',
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: '8px',
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                    >
                      {success}
                    </Alert>
                  )}

                  {/* OTP Input Fields */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1,
                      mb: 3
                    }}
                    onPaste={handlePaste}
                  >
                    {formData.otp.map((digit, index) => (
                      <TextField
                        key={index}
                        inputRef={(el) => (otpRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleOTPChange(index, e.target.value)}
                        disabled={loading || resendLoading}
                        inputProps={{
                          maxLength: 1,
                          onKeyDown: (
                            e: React.KeyboardEvent<HTMLInputElement>
                          ) => handleKeyDown(index, e),
                          style: {
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: 'bold'
                          }
                        }}
                        sx={{
                          width: 56,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 2,
                                borderColor: 'primary.main'
                              }
                            }
                          }
                        }}
                      />
                    ))}
                  </Box>

                  {/* Resend OTP Section */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {!canResend ? (
                      <Typography variant="body2" color="text.secondary">
                        Didn't receive the code? Resend in {countdown}s
                      </Typography>
                    ) : (
                      <Button
                        onClick={handleResendOTP}
                        disabled={resendLoading}
                        variant="text"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 'medium',
                          fontSize: '0.9rem'
                        }}
                      >
                        {resendLoading ? (
                          <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Resending...
                          </>
                        ) : (
                          'Resend OTP'
                        )}
                      </Button>
                    )}
                  </Box>
                </Box>

                <Box sx={{ p: 4, pt: 0 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      loading || formData.otp.some((digit) => digit === '')
                    }
                    fullWidth
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Verify OTP'
                    )}
                  </Button>

                  <Button
                    onClick={handleBackToForgotPassword}
                    disabled={loading || resendLoading}
                    variant="text"
                    fullWidth
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Back
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
}

export default OTPVerificationPage;
